// Tela de Cadastro de Produtos/Insumos (issue #8, campo Marca na #67) — padrão
// barra + tabela + modal.

import { newId } from "../../domain/ids.ts";
import type { Insumo } from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, el, tabelaPaginada, botao } from "../ui.ts";
import {
  abrirFormModal,
  barraCadastro,
  campo,
  campoObrigatorio,
  inputTexto,
  telaLista,
} from "./_cadastro.ts";
import { listaSelect } from "./_lista-select.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo, SITUACAO_CADASTRO } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

export function renderInsumos(
  container: HTMLElement,
  store: Store,
): () => void {
  // filtros, busca e página num estado só (#93): null = todos
  const lista = new EstadoLista({ categoria: null as string[] | null, situacao: null as string[] | null });
  const busca = buscaDaLista(lista, "Buscar por nome, marca, categoria ou fornecedor…", () => render());
  const fSituacao = filtroMultiplo(lista, "situacao", "Situação", () => render());
  const fCategoria = filtroMultiplo(lista, "categoria", "Categoria", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "configuracao");

    fCategoria.atualizar(snap.listas.categoriasInsumo.map((v) => ({ id: v, nome: v })));
    fSituacao.atualizar(SITUACAO_CADASTRO);
    const catFiltro = fCategoria.selecionados();
    const sitFiltro = fSituacao.selecionados();
    const visiveis = [...snap.insumos]
      .filter((i) => catFiltro.size === 0 || catFiltro.has(i.categoria))
      .filter((i) =>
        sitFiltro.size === 0 || sitFiltro.has(i.ativo ? "ativo" : "inativo"),
      )
      .filter((i) =>
        busca.casa(i.nome, i.categoria, i.marca, i.fornecedor),
      )
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const barra = barraCadastro(
      [fCategoria.element, fSituacao.element],
      "Novo Insumo",
      () => abrirModal(null),
      () => {
        lista.limpar();
        render();
      },
      undefined,
      podeGravar,
    );

    if (visiveis.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el(
            "p",
            { class: "muted lista-vazia" },
            lista.mensagemVazio({ nenhum: "Nenhum insumo.", filtrado: "Nenhum insumo encontrado." }),
          ),
        ),
      );
      return;
    }
    const linhas = visiveis.map((i) => {
      const acoes: Node[] = [];
      if (podeGravar) {
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirModal(i));
        const alternar = botao(
          "secundario",
          {},
          i.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store.executar(
            {
              agregado: "insumo",
              cmd: { tipo: "definirAtivo", id: i.id, ativo: !i.ativo },
            },
            i.ativo ? "Insumo inativado." : "Insumo reativado.",
          );
        });
        acoes.push(editar, alternar);
      }
      return el(
        "tr",
        { class: i.ativo ? "" : "inativo" },
        el("td", {}, i.nome),
        el("td", { class: "muted" }, i.marca ?? "—"),
        el("td", { class: "muted" }, i.categoria || "—"),
        el("td", { class: "muted" }, i.fornecedor ?? "—"),
        el(
          "td",
          {},
          i.ativo
            ? el("span", { class: "estado-ativo" }, "Ativo")
            : el("span", { class: "estado-inativo" }, "Inativo"),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });
    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          "insumos",
          ["Nome", "Marca", "Categoria", "Fornecedor", "Situação", ""],
          linhas,
          lista.tabela,
          render,
        ),
      ),
    );
  };

  const abrirModal = (atual: Insumo | null): void => {
    const nome = inputTexto("Nome do insumo", atual?.nome ?? "");
    const categoria = listaSelect(
      store,
      "categoriasInsumo",
      atual?.categoria ?? "",
    );
    const unidade = listaSelect(
      store,
      "unidadesInsumo",
      atual?.unidade ?? "",
    );
    const marca = inputTexto("Marca (opcional)", atual?.marca ?? "");
    const fornecedor = inputTexto("Fornecedor (opcional)", atual?.fornecedor ?? "");

    abrirFormModal({
      titulo: atual ? `Editar ${atual.nome}` : "Novo Insumo",
      rotuloSalvar: atual ? "Salvar" : "Criar Insumo",
      campos: () =>
        el(
          "div",
          {},
          campoObrigatorio("Nome", nome),
          campo("Marca", marca),
          campo("Categoria", categoria.element),
          campo("Unidade de medida", unidade.element),
          campo("Fornecedor", fornecedor),
        ),
      salvar: (aoErro) => {
        const dados = {
          nome: nome.value,
          categoria: categoria.value(),
          unidade: unidade.value(),
          marca: marca.value,
          fornecedor: fornecedor.value,
        };
        const id = atual?.id ?? newId();
        const cmd = atual
          ? ({ tipo: "editar", id, dados } as const)
          : ({ tipo: "criar", id, dados } as const);
        return store.executar({ agregado: "insumo", cmd }, undefined, aoErro);
      },
    });
  };

  render();
  return store.subscribe(render);
}
